import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUnreadCount } from '../store/notificationSlice';
import { startNotificationStream } from '../services';
import { createNotificationSoundManager, enableAudio } from './audioUtils';

export const GLOBAL_NEW_NOTIFICATION_EVENT = 'pmpi:new-notification';

export const isSelectedProjectNotification = (selectedProjectId, notificationProjectId) => {
  const normalizedSelectedProjectId = String(selectedProjectId ?? '').trim().toLowerCase();

  // An "All projects" selection intentionally includes notifications from every project.
  if (!normalizedSelectedProjectId || normalizedSelectedProjectId === 'all') {
    return true;
  }

  // Do not play a project-specific sound when the incoming notification has no project context.
  if (notificationProjectId === null || notificationProjectId === undefined || notificationProjectId === '') {
    return false;
  }

  return String(notificationProjectId).trim() === String(selectedProjectId).trim();
};

const GlobalNotificationListener = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, accessToken, user } = useSelector((state) => state.auth || {});
  const selectedProjectId = useSelector((state) => state.tickets?.filters?.selectedProject);
  const audioUnlockedRef = useRef(false);
  const selectedProjectIdRef = useRef(selectedProjectId);

  const soundManager = useMemo(() => createNotificationSoundManager(), []);

  // 1. Attach global interaction listeners to unlock audio on first user gesture across any screen
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (audioUnlockedRef.current) return;
      const success = await enableAudio();
      if (success) {
        audioUnlockedRef.current = true;
      }
      soundManager.syncDebugState();
    };

    // Try initial unlock attempt in case browser allows it
    handleUserInteraction();

    window.addEventListener('click', handleUserInteraction, { capture: true, passive: true });
    window.addEventListener('keydown', handleUserInteraction, { capture: true, passive: true });
    window.addEventListener('touchstart', handleUserInteraction, { capture: true, passive: true });
    window.addEventListener('mousedown', handleUserInteraction, { capture: true, passive: true });

    return () => {
      window.removeEventListener('click', handleUserInteraction, { capture: true });
      window.removeEventListener('keydown', handleUserInteraction, { capture: true });
      window.removeEventListener('touchstart', handleUserInteraction, { capture: true });
      window.removeEventListener('mousedown', handleUserInteraction, { capture: true });
    };
  }, [soundManager]);

  const isBaselineRef = useRef(true);
  const previousUnreadCountByProjectRef = useRef({});

  // The stream callback is long-lived, so read the current project from a ref rather than
  // reconnecting the stream whenever the project selector changes.
  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  // 2. Manage notification stream and audio triggers globally
  useEffect(() => {
    const userId = user?.id || user?.user_id;
    if (!isAuthenticated || !accessToken || !userId) {
      return undefined;
    }

    isBaselineRef.current = true;
    previousUnreadCountByProjectRef.current = {};

    soundManager.attach();

    const stopStream = startNotificationStream({
      onUnreadCount: (unreadCount, newNotificationCount, notificationData) => {
        dispatch(setUnreadCount({ count: unreadCount, projectId: notificationData?.projectId }));

        const hasExplicitNew = typeof newNotificationCount === 'number' && newNotificationCount > 0;
        const projectIdContext = notificationData?.projectId || 'global';
        const prevCount = previousUnreadCountByProjectRef.current[projectIdContext] ?? null;

        // Skip sound on initial baseline message only if there are no explicit new notifications
        if (isBaselineRef.current && !hasExplicitNew) {
          isBaselineRef.current = false;
          previousUnreadCountByProjectRef.current[projectIdContext] = unreadCount;
          return;
        }

        isBaselineRef.current = false;

        // For live pushed messages over active SSE connection:
        let effectiveNewCount = 0;

        if (hasExplicitNew) {
          effectiveNewCount = newNotificationCount;
        } else if (prevCount !== null) {
          effectiveNewCount = unreadCount > prevCount ? unreadCount - prevCount : 0;
        } else if (notificationData && unreadCount > 0) {
          // Fallback: live SSE message pushed without prior baseline count represents a live notification event
          effectiveNewCount = 1;
        } else {
          effectiveNewCount = 0;
        }

        previousUnreadCountByProjectRef.current[projectIdContext] = unreadCount;

        if (
          effectiveNewCount > 0
          && isSelectedProjectNotification(selectedProjectIdRef.current, notificationData?.projectId)
        ) {
          soundManager.handleNewNotification(effectiveNewCount, notificationData?.projectId ?? null);
        }

        // Dispatch window custom event so individual screens can listen to state updates
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(GLOBAL_NEW_NOTIFICATION_EVENT, {
              detail: { unreadCount, newNotificationCount: effectiveNewCount, notificationData }
            })
          );
        }
      },
      onAiResponseCompleted: (data, message) => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('pmpi:ai-response-completed', {
              detail: { data, message }
            })
          );
        }
      }
    });

    return () => {
      soundManager.cleanup();
      if (typeof stopStream === 'function') {
        stopStream();
      }
    };
  }, [accessToken, dispatch, isAuthenticated, soundManager, user?.id, user?.user_id]);

  return <>{children}</>;
};

export default GlobalNotificationListener;
